"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import {
  FaFacebookF,
  FaXTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaPinterest,
  FaYoutube,
  FaMedium,
  FaCaretDown,
} from "react-icons/fa6";

import logo from "@/public/images/logo/trichos-logo.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(
    {}
  );

  const handleToggleCategory = (categoryName: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  const socialLinks = [
    {
      href: "https://www.facebook.com/TrichosHairTransplant",
      icon: FaFacebookF,
      label: "Facebook",
    },
    {
      href: "https://www.twitter.com/TrichosCare",
      icon: FaXTwitter,
      label: "Twitter",
    },
    {
      href: "https://www.instagram.com/dr.johnwatts",
      icon: FaInstagram,
      label: "Instagram",
    },
    {
      href: "https://www.linkedin.com/in/dr-john-watts-1aa01a55/",
      icon: FaLinkedinIn,
      label: "LinkedIn",
    },
    {
      href: "https://in.pinterest.com/trichoshairtranplant",
      icon: FaPinterest,
      label: "Pinterest",
    },
    {
      href: "https://www.youtube.com/@dr.johnwatts",
      icon: FaYoutube,
      label: "YouTube",
    },
    {
      href: "https://medium.com/@Dr.Johnwatts",
      icon: FaMedium,
      label: "Medium",
    },
  ];

  // Column 1: Hair Transplant Consultation
  const consultationLinks = [
    {
      name: "Hair Transplant Consultation",
      href: "/campaigns/hair-transplant-consultation",
    },
    {
      name: "Hair Transplant in Hyderabad",
      href: "/hair-transplant/best-hair-transplant-center-hyderabad",
    },
    {
      name: "Cost of Hair Transplant in Hyderabad",
      href: "/hair-transplants/hair-transplant-cost-hyderabad",
    },
    {
      name: "Hair Transplant in Men",
      href: "/hair-transplant-research/hair-transplant-in-men",
    },
    {
      name: "Hair Transplant in Women",
      href: "/hair-transplant-research/hair-transplant-in-women",
    },
    {
      name: "Artificial Hair Transplant",
      href: "/hair-transplant-research/artificial-hair-replacement-systems",
    },
  ];

  const planningLinks = [
    {
      name: "What Happens on Surgery Day",
      href: "/hair-transplant-planning/hair-transplant-surgery-day-trichos",
    },
    {
      name: "Pre-Surgery Instructions",
      href: "/hair-transplant-planning/pre-surgery-hair-transplant-instructions",
    },
    {
      name: "Post-Surgery Instructions",
      href: "/hair-transplant-planning/post-hair-transplantation-instructions",
    },
    { name: "Consent Form", href: "/" },
  ];

  // Column 2: Scalp Hair Transplant
  const scalpLinks = [
    { name: "Scalp Hair Transplant", href: "/hair-transplant" },
    {
      name: "Hairline Hair Transplant",
      href: "/hair-transplant/hairline-hair-transplant-in-hyderabad",
    },
    {
      name: "Crown Hair Transplant",
      href: "/hair-transplant/crown-hair-transplant-hyderabad",
    },
    {
      name: "Advanced FUE Hair Transplant",
      href: "/hair-transplant/advanced-fue-hair-transplant",
    },
    {
      name: "Non-Shaven Long Hair Transplant",
      href: "/hair-transplant/long-hair-transplant-in-hyderabad-dr-john-watts",
      badge: "Latest",
    },
    {
      name: "Failed Repair Hair Transplant",
      href: "/hair-transplant/corrective-hair-transplant",
    },
  ];

  // Column 3: FUE Hair Transplant
  const fueLinks = [
    {
      name: "FUE Hair Transplant",
      href: "/hair-transplant/fue-hair-transplant-hyderabad",
    },
    {
      name: "Advanced FUE Hair Transplant",
      href: "/hair-transplant/advanced-fue-hair-transplant",
    },

    {
      name: "Body Hair Transplant",
      href: "/facial-hair-transplant/body-hair-transplant-hyderabad",
    },
    {
      name: "Failed Repair Hair Transplant",
      href: "/hair-transplant/corrective-hair-transplant",
    },
  ];

  const techniqueLinks = [
    {
      name: "Hair Biology",
      href: "/hair-and-scalp-disorders/understanding-hair-biology-before-addressing-hair-loss",
    },
    {
      name: "DHT Hormone",
      href: "/hormonal-and-systemic-conditions/hair-fall-prevention-dht-blocker",
    },
  ];

  // Column 4: Facial Hair Transplant
  const facialLinks = [
    {
      name: "Facial Hair Transplant",
      href: "/facial-hair-transplant/trichos-solutions-facial-hair-transplant",
    },
    {
      name: "Beard & Moustache Hair Transplant",
      href: "/facial-hair-transplant/beard-and-moustache-hair-transplant",
    },
    {
      name: "Eyebrow Hair Transplant",
      href: "/facial-hair-transplant/eyebrow-hair-transplant",
    },
    {
      name: "Burn Hair Transplant",
      href: "/facial-hair-transplant/burn-hair-transplant",
    },
    {
      name: "Body Hair Transplant",
      href: "/facial-hair-transplant/body-hair-transplant-hyderabad",
    },
  ];

  // Column 5: Non-Surgical, Results, Knowledge Hub, About
  const nonSurgicalLinks = [
    {
      name: "GFC for Hair",
      href: "/non-surgical-solutions/gfc-therapy-hair-restoration-trichos",
      badge: "Trending",
    },
    {
      name: "Mesotherapy",
      href: "/non-surgical-solutions/best-mesotherapy-treatment-hair-loss-hyderabad",
    },
    {
      name: "Hair Art & Scalp Micropigmentation",
      href: "/non-surgical-solutions/scalp-micropigmentation",
    },
    {
      name: "MicroRoller",
      href: "/non-surgical-solutions/micro-needling-hair-regrowth-trichos",
    },
  ];

  const resultsLinks = [
    { name: "Before & After Photos", href: "/gallery" },
    {
      name: "Client Transformation Videos",
      href: "/client-transformation-videos",
    },
    { name: "Success Stories", href: "/patient-testimony" },
    { name: "Press & Media Centre", href: "/dr-john-watts-media" },
  ];

  const hairLossCausesLinks = [
    {
      name: "Climatic Conditions",
      href: "/hair-and-scalp-disorders/alopecia-preventing-hair-loss-from-external-factors",
    },
    {
      name: "Hereditary",
      href: "/hair-and-scalp-disorders/hereditary-hair-loss-treatment-hyderabad",
    },
    {
      name: "Nutritional Deficiencies",
      href: "/hair-and-scalp-disorders/nutritional-deficiency-hair-growth-supplements",
    },
    {
      name: "Autoimmune Disorders",
      href: "/hair-and-scalp-disorders/alopecia-autoimmune-hair-loss-remedies",
    },
    {
      name: "Acute Stress Disorder",
      href: "/hair-and-scalp-disorders/understanding-stress-induced-hair-loss-treatment-options",
    },
    {
      name: "Chemical Coloration",
      href: "/hair-and-scalp-disorders/hair-dye-induced-hair-loss-remedies",
    },
  ];

  const commonScalpConditionsLinks = [
    {
      name: "Dandruff",
      href: "/common-scalp-conditions/stop-dandruff-hair-loss-trichos-care",
    },
    {
      name: "Dermatitis",
      href: "/common-scalp-conditions/effective-solutions-for-dermatitis-hair-loss",
    },
    {
      name: "Scalp Folliculitis",
      href: "/common-scalp-conditions/alopecia-scalp-folliculitis-treatment-trichos",
    },
    {
      name: "Folliculitis Decalvans",
      href: "/common-scalp-conditions/alopecia-folliculitis-decalvans-hair-loss-solution",
    },
    {
      name: "Seborrheic Dermatitis",
      href: "/common-scalp-conditions/seborrheic-dermatitis-treatment-hyderabad",
    },
    {
      name: "Eczema",
      href: "/common-scalp-conditions/hair-loss-eczema-treatment-hyderabad",
    },
    {
      name: "Pseudopelade",
      href: "/common-scalp-conditions/trichos-solution-pseudopelade-of-brocq",
    },
    {
      name: "Cradle Cap",
      href: "/common-scalp-conditions/cradle-cap-treatment-in-hyderabad",
    },
  ];

  const specificHairScalpDisordersLinks = [
    {
      name: "Psoriasis",
      href: "/specific-hair-and-scalp-disorders/alopecia-scalp-psoriasis-treatment-hyderabad",
    },
    {
      name: "Lichen Simplex Chronicus Scalp",
      href: "/specific-hair-and-scalp-disorders/lichen-simplex-chronicus-treatment-hyderabad",
    },
    {
      name: "Discoid Lupus Erythematosus",
      href: "/specific-hair-and-scalp-disorders/effective-treatment-discoid-lupus-erythematosus",
    },
    {
      name: "Lichen Planopilaris",
      href: "/specific-hair-and-scalp-disorders/best-treatment-for-lichen-planopilaris",
    },
    {
      name: "Pityriasis Amiantacea",
      href: "/specific-hair-and-scalp-disorders/pityriasis-amiantacea-hair-treatment-hyderabad",
    },
    {
      name: "Trichorrhexis Nodosa",
      href: "/specific-hair-and-scalp-disorders/trichorrhexis-nodosa-treatment-hyderabad",
    },
    {
      name: "Plica Polonicia",
      href: "/specific-hair-and-scalp-disorders/alopecia-plica-polonica-treatment",
    },
  ];

  const hormonalSystemicConditionsLinks = [
    {
      name: "Hirsutism",
      href: "/hormonal-and-systemic-conditions/hirsutism-unwanted-hair-growth-treatment-trichos",
    },
    {
      name: "Thyroid Hormone",
      href: "/hormonal-and-systemic-conditions/baldness-treatment-thyroid-hair-loss",
    },
    {
      name: "DHT Hormone",
      href: "/hormonal-and-systemic-conditions/hair-fall-prevention-dht-blocker",
    },
  ];

  const infectionLinks = [
    {
      name: "Fungal Infection",
      href: "/hair-and-scalp-infections/fungal-infection-tinea-capitis-treatment-hyderabad",
    },
    {
      name: "Bacterial Infection",
      href: "/hair-and-scalp-infections/bacterial-infection-hair-loss-remedies-trichos-hyderabad",
    },
  ];

  const alopeciaTypesLinks = [
    {
      name: "Androgenic Alopecia",
      href: "/alopecia-condition/best-androgenetic-alopecia-treatment-hyderabad",
    },
    {
      name: "Alopecia Areata",
      href: "/alopecia-condition/alopecia-areata-treatment-hyderabad",
    },
    {
      name: "Traction Alopecia",
      href: "/alopecia-condition/best-treatment-for-traction-alopecia-hyderabad",
    },
    {
      name: "Telogen Effluvium",
      href: "/alopecia-condition/telogen-effluvium-treatment-hyderabad",
    },
    {
      name: "Cicatricial Alopecia",
      href: "/alopecia-condition/cicatricial-alopecia-treatment-hyderabad",
    },
    {
      name: "Alopecia Totalis",
      href: "/alopecia-condition/alopecia-totalis-symptoms-and-treatments",
    },
    {
      name: "Alopecia Universalis",
      href: "/alopecia-condition/alopecia-universalis-understanding-treatment",
    },
    {
      name: "Anagen Effluvium",
      href: "/alopecia-condition/anagen-effluvium-treatment-hair-loss",
    },
    {
      name: "Involutional Alopecia",
      href: "/alopecia-condition/involutional-alopecia-treatment-hyderabad",
    },
    {
      name: "Tinea Capitis",
      href: "/alopecia-condition/tinea-capitis-treatment-hyderabad",
    },
  ];

  const aboutLinks = [
    {
      name: "About Trichos",
      href: "/about-trichos/affordable-hair-restoration-hyderabad",
    },
    { name: "Founders Profile", href: "/dermatologist/dr-john-watts" },
    { name: "Our Blogs", href: "/blog" },
    { name: "Contact Us", href: "/about-trichos/contact-us" },
  ];

  // Mapping category titles to their slugs
  const categorySlugMap: Record<string, string> = {
    "Hair Transplant Consultation": "/hair-transplant",
    "Scalp Hair Transplant": "/hair-transplant",
    "FUE Hair Transplant": "/hair-transplant",
    "Facial Hair Transplant": "/facial-hair-transplant",
    "Non-Surgical & Alternatives": "/non-surgical-solutions",
    "Hair Loss Causes": "/hair-and-scalp-disorders",
    "Scalp Conditions": "/common-scalp-conditions",
    Infections: "/hair-and-scalp-infections",
    "Specific Hair and Scalp Disorders": "/specific-hair-and-scalp-disorders",
    "Alopecia Types": "/alopecia-condition",
  };

  const renderLinkColumn = (
    title: string,
    links: Array<{ name: string; href: string; badge?: string }>,
    subSection?: {
      title: string;
      links: Array<{ name: string; href: string; badge?: string }>;
    },
    subSections?: Array<{
      title: string;
      links: Array<{ name: string; href: string; badge?: string }>;
    }>
  ) => {
    const isOpen = openCategories[title] ?? false;
    const sectionsToRender = subSections || (subSection ? [subSection] : []);
    const categoryHref = categorySlugMap[title] || "#";

    return (
      <div>
        <div className="mb-4 flex w-full items-center justify-between md:justify-start">
          <Link
            href={categoryHref}
            onClick={(e) => {
              // On mobile, toggle the category instead of navigating
              if (window.innerWidth < 768) {
                e.preventDefault();
                handleToggleCategory(title);
              }
            }}
            className="flex flex-1 items-center justify-between text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:text-gray-300 md:justify-start"
          >
            <span>{title}</span>
            <FaCaretDown
              className={`ml-2 transition-transform duration-200 md:hidden ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </Link>
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isOpen
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0 md:max-h-[2000px] md:opacity-100"
          }`}
        >
          <ul className="space-y-3">
            {links.map((link) => (
              <li key={link.name} className="flex items-center gap-2">
                <Link
                  href={link.href}
                  className="text-sm text-gray-400 transition-colors hover:text-white"
                >
                  {link.name}
                </Link>
                {link.badge && (
                  <span className="inline-flex items-center rounded bg-sky-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-sky-800">
                    {link.badge}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {sectionsToRender.length > 0 && (
            <div className="mt-6 space-y-6">
              {sectionsToRender.map((section, index) => (
                <div key={index}>
                  <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {section.title}
                  </h4>
                  <ul className="space-y-2">
                    {section.links.map((link) => (
                      <li key={link.name} className="flex items-center gap-2">
                        <Link
                          href={link.href}
                          className="text-sm text-gray-400 transition-colors hover:text-white"
                        >
                          {link.name}
                        </Link>
                        {link.badge && (
                          <span className="inline-flex items-center rounded bg-sky-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-sky-800">
                            {link.badge}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCollapsibleColumn = (
    title: string,
    children: React.ReactNode
  ) => {
    const isOpen = openCategories[title] ?? false;
    const categoryHref = categorySlugMap[title] || "#";

    return (
      <div>
        <div className="mb-4 flex w-full items-center justify-between md:justify-start">
          <Link
            href={categoryHref}
            onClick={(e) => {
              // On mobile, toggle the category instead of navigating
              if (window.innerWidth < 768) {
                e.preventDefault();
                handleToggleCategory(title);
              }
            }}
            className="flex flex-1 items-center justify-between text-sm font-semibold uppercase tracking-wide text-gray-300 transition-colors hover:text-white md:justify-start"
          >
            <span>{title}</span>
            <FaCaretDown
              className={`ml-2 transition-transform duration-200 md:hidden ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </Link>
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isOpen
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0 md:max-h-[2000px] md:opacity-100"
          }`}
        >
          {children}
        </div>
      </div>
    );
  };

  return (
    <footer className="bg-black text-white">
      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 lg:px-8">
        {/* Company Info & Social Media */}
        <div className="mb-12 border-b border-gray-800 pb-8">
          <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
            <div className="flex-1">
              <Link href="/" className="mb-4 inline-block">
                <Image
                  src={logo}
                  width={160}
                  height={40}
                  alt="Trichos Logo"
                  quality={100}
                  priority
                  unoptimized
                  className="mb-2"
                />
                <p className="mt-1 text-sm text-gray-400">
                  Hair Transplant & Hair Loss Clinic, Hyderabad
                </p>
              </Link>
              <p className="mt-4 text-sm leading-relaxed text-gray-300">
                Hair transplant centre offering FUE, NSLHT and other hair
                restoration procedures in Hyderabad, India.
              </p>
            </div>

            {/* Social Media Icons */}
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Follow Us
              </p>
              <div className="flex flex-wrap gap-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <Link
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex size-10 items-center justify-center rounded-full bg-gray-800 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
                      aria-label={social.label}
                    >
                      <Icon className="size-5" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* 5 Column Footer Links */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {/* Column 1: Hair Transplant Consultation */}
          {renderLinkColumn("Hair Transplant Consultation", consultationLinks, {
            title: "Planning & Trust",
            links: planningLinks,
          })}

          {/* Column 2: Scalp Hair Transplant */}
          {renderLinkColumn("Scalp Hair Transplant", scalpLinks)}

          {/* Column 3: FUE Hair Transplant */}
          {renderLinkColumn("FUE Hair Transplant", fueLinks, {
            title: "Technique Knowledge",
            links: techniqueLinks,
          })}

          {/* Column 4: Facial Hair Transplant */}
          {renderLinkColumn("Facial Hair Transplant", facialLinks)}

          {/* Column 5: Non-Surgical */}
          {renderLinkColumn(
            "Non-Surgical & Alternatives",
            nonSurgicalLinks,
            undefined,
            [
              {
                title: "Results & Trust",
                links: resultsLinks,
              },
              {
                title: "About Trichos",
                links: aboutLinks,
              },
            ]
          )}
        </div>

        {/* Hair Loss Knowledge Hub - Separate Section with 5 Columns */}
        <div className="mt-12 border-t border-gray-800 pt-12">
          <h3 className="mb-8 text-center text-lg font-semibold uppercase tracking-wide text-white md:text-left">
            Hair Loss Knowledge Hub
          </h3>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {/* Column 1: Hair Loss Causes */}
            {renderCollapsibleColumn(
              "Hair Loss Causes",
              <ul className="space-y-3">
                {hairLossCausesLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="text-sm text-gray-400 transition-colors hover:text-white"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* Column 2: Scalp Conditions */}
            {renderCollapsibleColumn(
              "Scalp Conditions",
              <div>
                <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Common Scalp Conditions
                </h4>
                <ul className="space-y-3">
                  {commonScalpConditionsLinks.map((link) => (
                    <li key={link.name}>
                      <Link
                        href={link.href}
                        className="text-sm text-gray-400 transition-colors hover:text-white"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Column 3: Infections */}
            {renderCollapsibleColumn(
              "Infections",
              <div className="space-y-6">
                <ul className="space-y-3">
                  {infectionLinks.map((link) => (
                    <li key={link.name}>
                      <Link
                        href={link.href}
                        className="text-sm text-gray-400 transition-colors hover:text-white"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>

                {/* Hormonal and Systemic Conditions */}
                <div>
                  <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Hormonal and Systemic Conditions
                  </h4>
                  <ul className="space-y-3">
                    {hormonalSystemicConditionsLinks.map((link) => (
                      <li key={link.name}>
                        <Link
                          href={link.href}
                          className="text-sm text-gray-400 transition-colors hover:text-white"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Column 4: Specific Hair and Scalp Disorders */}
            {renderCollapsibleColumn(
              "Specific Hair and Scalp Disorders",
              <ul className="space-y-3">
                {specificHairScalpDisordersLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="text-sm text-gray-400 transition-colors hover:text-white"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* Column 5: Alopecia Types */}
            {renderCollapsibleColumn(
              "Alopecia Types",
              <ul className="space-y-3">
                {alopeciaTypesLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="text-sm text-gray-400 transition-colors hover:text-white"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Disclaimer Section */}
        <div className="mt-12 border-t border-gray-800 pt-8">
          <p className="text-center text-xs text-gray-400">
            Treatment suitability and outcomes depend on individual medical
            evaluation. Results may vary from person to person.
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 border-t border-gray-800 pt-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            {/* Copyright */}
            <div className="text-center text-sm text-gray-400 md:text-left">
              <p>
                © {currentYear} Trichos Hair Transplant Center. All rights
                reserved.
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <Link
                href="/privacy-policy"
                className="text-gray-400 transition-colors hover:text-white"
              >
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link
                href="/cookie-policy"
                className="text-gray-400 transition-colors hover:text-white"
              >
                Cookie Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link
                href="/disclaimers"
                className="text-gray-400 transition-colors hover:text-white"
              >
                Disclaimer
              </Link>
              <span className="text-gray-600">|</span>
              <Link
                href="/terms-conditions"
                className="text-gray-400 transition-colors hover:text-white"
              >
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
